import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Star,
  Check,
  Play,
  Clock,
  Users,
  Zap,
  TrendingUp,
  Heart,
  Video,
  ChevronDown,
  Music,
  MessageSquare,
  Shield,
  Sparkles,
  Facebook,
  Instagram,
  Radio
} from 'lucide-react';

import unlimitedLearning from '../../assets/unlimitedLearning.mp4';
import landingGif from '../../assets/landingGif.mp4';
import funpart from '../../assets/funpart.jpeg';
import communityPart from '../../assets/communityPart.png';
import { SVGUpdatedLogoC } from '../../assets/PackageSVG';

const HomePage = () => {
  const navigate = useNavigate();

  const [openFaq, setOpenFaq] = useState(null);

  const faqData = [
    { q: 'Are Listing Party sessions virtual?', a: "Yes! All Listing Party sessions are 100% virtual. Join from anywhere—your home office, your warehouse, or even on the go. All you need is an internet connection and you're ready to connect with the community." },
    { q: 'How much does Listing Party cost?', a: "Listing Party is completely free for all List Perfectly members. It's included with any List Perfectly plan—no extra fees, no hidden costs. Just sign in and start joining sessions." },
    { q: 'Do I need to be on camera during sessions?', a: 'Nope! Camera is completely optional. Many members prefer to keep their camera off and just participate through chat or by listening. Join in whatever way feels comfortable for you.' },
    { q: 'Do I need to be on mic during sessions?', a: 'No, being on mic is optional. You can participate through text chat if you prefer. However, if you choose to speak, please be respectful and follow session guidelines.' },
    { q: 'What are the etiquette and rules?', a: 'We maintain a respectful, supportive environment. Members must follow community guidelines including being respectful to all participants, staying on topic, and creating a positive learning space. Session hosts will share specific guidelines at the start of each session.' },
    { q: 'What happens at the beginning of each session?', a: 'At the start of every session, participants must accept and agree to follow Listing Party community guidelines and rules. This ensures everyone is aligned on creating a safe, productive learning environment.' },
    { q: 'Can I be removed from Listing Party?', a: 'Yes. Listing Party reserves the right to remove members who violate community guidelines, engage in disrespectful behavior, or create an unsafe environment for other participants. We are committed to maintaining a positive, supportive community for all resellers.' }
  ];

  return (
    <div className="min-h-screen w-full bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-900 via-purple-900 to-blue-800 text-white overflow-hidden">
        <div className="container mx-auto px-4 sm:px-6 py-12 sm:py-20 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* LEFT: Text Content */}
            <div className="order-1">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-full mb-6 sm:mb-8 shadow-lg text-xs sm:text-sm">
                <Star className="w-4 h-4 sm:w-5 sm:h-5 fill-current flex-shrink-0" />
                <span className="font-semibold whitespace-nowrap sm:whitespace-normal md:text-xs text-[12px]">Join 1,000s of resellers learning together</span>
              </div>

              <h1 className="text-3xl sm:text-4xl md:text-3xl lg:text-4xl xl:text-5xl font-bold mb-6 sm:mb-8 leading-tight">
                List, Learn and Grow<br />
                <span className="bg-gradient-to-r from-emerald-400 via-cyan-400 to-blue-400 bg-clip-text text-transparent">
                  With Community and Education
                </span>
              </h1>

              <p className="text-base sm:text-lg lg:text-xl text-gray-200 mb-8 sm:mb-12 leading-relaxed">
                Join live sessions with a supportive community of online resellers. Learn marketplace strategies, get expert feedback, and build the skills that grow your business.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-6 sm:mb-8">
                <button 
                  onClick={() => navigate('/login')}
                  className="bg-gradient-to-r from-emerald-400 to-cyan-400 text-gray-900 hover:scale-105 transition-transform rounded-full px-6 sm:px-8 lg:px-10 py-3 sm:py-4 text-sm font-bold shadow-xl flex items-center justify-center gap-2"
                >
                  <Play className="w-4 h-4 sm:w-5 sm:h-5 fill-current" />
                  Sign In & Join a Session
                </button>
                <button 
                  onClick={() => window.open('https://listperfectly.com/pricing/', '_blank')}
                  className="bg-white text-blue-900 rounded-full px-6 sm:px-8 lg:px-10 py-3 sm:py-4 text-sm font-bold hover:scale-105 transition-transform shadow-xl"
                >
                  Start with List Perfectly
                </button>
              </div>

              <div className="flex flex-wrap gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5" />
                  <span>Free education for all LP members</span>
            </div>
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5" />
                  <span>Available 24/7</span>
          </div>
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5" />
                  <span>Safe learning environment</span>
          </div>
        </div>
      </div>

            {/* RIGHT: Image */}
            <div className="order-2">
              <video
                  src={landingGif}
                  alt="Dashboard"
                  className="w-full h-full border-4 border-purple-700 rounded-xl object-cover"
                  autoPlay
                  loop
                  muted
                  playsInline
              />
            </div>
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 text-gray-900">What Is Listing Party?</h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed px-4">
              Listing Party is your online community for learning and growing your reselling business. Join live sessions where resellers work together, share tips, and support each other. It's education that fits your schedule—fun, low-stress, and designed around your business needs. Whether you're listing, learning new strategies, or connecting with other resellers, Listing Party makes growing your business easier and more enjoyable.
          </p>
        </div>

          <div className="max-w-5xl mx-auto">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-gray-200 aspect-video">
              <iframe
                className="w-full h-full"
                src="https://www.youtube.com/embed/ZwQnsxDtSFk"
                title="Listing Party Education Demo"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
      </div>

            <div className="flex items-center justify-center gap-2 mt-6 sm:mt-8 text-gray-600 px-4">
              <Video className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
              <span className="text-sm sm:text-base text-center">See how resellers learn together, share strategies, and improve their skills</span>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-gradient-to-r from-emerald-500 to-cyan-500 py-8 sm:py-12">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8 text-center text-white">
            <div>
              <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-1 sm:mb-2">100-200%</div>
              <div className="text-xs sm:text-sm lg:text-lg font-semibold opacity-90">Average Sales Growth</div>
            </div>
            <div>
              <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-1 sm:mb-2">24/7</div>
              <div className="text-xs sm:text-sm lg:text-lg font-semibold opacity-90">Sessions Available</div>
            </div>
            <div>
              <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-1 sm:mb-2">1000s</div>
              <div className="text-xs sm:text-sm lg:text-lg font-semibold opacity-90">Active Members</div>
            </div>
            <div>
              <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-1 sm:mb-2">FREE</div>
              <div className="text-xs sm:text-sm lg:text-lg font-semibold opacity-90">For All List Perfectly Users</div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-12 sm:py-16 lg:py-24 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12 lg:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 text-gray-900">
              Reselling Alone Is Hard.<br />
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                It Doesn't Have To Be.
              </span>
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 max-w-6xl mx-auto">
            <div className="p-6 sm:p-8 border-2 rounded-xl bg-white">
              <h3 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 text-gray-900">Without Community</h3>
              <ul className="space-y-4">
                {['Unorganized workflow leads to wasted time', 'Overworked and overwhelmed with no support system', 'Isolated—no one to celebrate wins or troubleshoot problems', 'Guessing what works instead of learning proven methods', 'Motivation fades when working alone'].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-red-100 text-red-600 flex items-center justify-center flex-shrink-0 mt-1 font-bold text-sm">✗</div>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="p-6 sm:p-8 bg-gradient-to-br from-emerald-50 to-cyan-50 border-4 border-emerald-400 shadow-xl rounded-xl">
              <h3 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 text-gray-900">With Listing Party</h3>
              <ul className="space-y-4">
                {['Fun and social—work alongside friendly resellers', 'Low-stress environment where you can learn at your own pace', 'Flexible sessions that fit your business and schedule', 'Learn strategies that actually work from experienced resellers', 'Grow faster with support every step of the way'].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-emerald-500 text-white flex items-center justify-center flex-shrink-0 mt-1 font-bold text-sm">✓</div>
                    <span className="text-gray-700"><strong>{item.split('—')[0]}</strong>{item.includes('—') ? '—' + item.split('—')[1] : ''}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Screenshots Section */}
      <section className="py-12 sm:py-16 lg:py-24 bg-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12 lg:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 text-gray-900">Inside Listing Party</h2>
          </div>

          <div className="space-y-12 sm:space-y-16 lg:space-y-[25vh]">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
              <div className="rounded-2xl aspect-video flex items-center justify-center shadow-2xl border-4 border-blue-200 overflow-hidden">
                <video 
                  src={unlimitedLearning} 
                  alt="Dashboard" 
                  className="w-full h-full object-cover"
                  autoPlay
                  loop
                  muted
                  playsInline
                />
              </div>
              <div>
                <span className="inline-block bg-blue-100 text-blue-800 px-3 sm:px-4 py-1 sm:py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4">Education Hub</span>
                <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-3 sm:mb-4 text-gray-900">Unlimited Learning Opportunities</h3>
                <p className="text-base sm:text-lg text-gray-600 mb-4 sm:mb-6">Access unlimited educational sessions covering every aspect of online reselling. Learn at your own pace with live sessions designed to help you list faster and more effectively.</p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
              <div className="order-2 lg:order-1">
                <span className="inline-block bg-purple-100 text-purple-800 px-3 sm:px-4 py-1 sm:py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4">Live Sessions</span>
                <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-3 sm:mb-4 text-gray-900">The Fun Part of Every Session</h3>
                <p className="text-base sm:text-lg text-gray-600 mb-4 sm:mb-6">Join live sessions where the energy is positive, the vibe is supportive, and you're surrounded by resellers who get it. Share laughs, celebrate wins, and enjoy working with people who make listing feel less like work.</p>
              </div>
              <div className="rounded-2xl aspect-video flex items-center justify-center shadow-2xl border-4 border-purple-200 overflow-hidden order-1 lg:order-2">
                <img src={funpart} alt="Live Session" className="w-full h-full object-cover" />
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
              <div
                  className="rounded-2xl aspect-video flex items-center justify-center shadow-2xl border-4 border-purple-200 overflow-hidden">
                <img src={communityPart} alt="Live Session" className="w-full h-full object-cover"/>
              </div>
              <div>
                <span
                    className="inline-block bg-emerald-100 text-emerald-800 px-3 sm:px-4 py-1 sm:py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4">Learning Community</span>
                <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-3 sm:mb-4 text-gray-900">Continuous
                  Education & Support</h3>
                <p className="text-base sm:text-lg text-gray-600 mb-4 sm:mb-6">Stay connected with the learning
                  community between sessions. Access educational resources, share discoveries, and learn from the
                  collective knowledge of thousands of successful resellers.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-12 sm:py-16 lg:py-24 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12 lg:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 text-gray-900">Why Join Listing Party?</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {[
              { icon: Check, title: 'Learn Best Practices', desc: 'Master listing optimization, inventory management, scaling your business, calculating pricing, writing rich descriptions, and more.' },
              { icon: Clock, title: 'List More Consistently', desc: 'Build a sustainable listing habit through scheduled educational sessions. No more sporadic bursts followed by weeks of nothing.' },
              { icon: Users, title: 'Connect With Community', desc: 'Join a supportive network of resellers who understand your journey. Share experiences, celebrate wins, and grow together through collective wisdom and encouragement.' },
              { icon: Zap, title: 'Get Expert Feedback', desc: 'Improve your reselling processes with expert advice on listing workflow, listing optimization, and reselling strategy.' },
              { icon: TrendingUp, title: 'Grow Your Sales', desc: 'Members consistently report 100-200%+ sales increases. Better education + consistent habits = real revenue growth.' },
              { icon: Heart, title: 'Stay Motivated', desc: 'Learning alone is lonely and draining. Our community education keeps you engaged even when motivation is low.' }
            ].map((benefit, i) => {
              const Icon = benefit.icon
              return (
                <div key={i} className="p-6 sm:p-8 bg-white rounded-xl hover:shadow-2xl hover:-translate-y-2 transition-all border-2">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center text-white mb-4 sm:mb-6">
                    <Icon className="w-6 h-6 sm:w-8 sm:h-8" />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-gray-900">{benefit.title}</h3>
                  <p className="text-sm sm:text-base text-gray-600">{benefit.desc}</p>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-12 sm:py-16 lg:py-24 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12 lg:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 text-gray-900">Getting Started Is Easy</h2>
      </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 sm:gap-10 lg:gap-12 max-w-5xl mx-auto mb-8 sm:mb-12">
            {[
              { num: '1', title: 'Sign Up', desc: 'Choose any List Perfectly plan and get instant access to Listing Party. All plans include full educational access.', bg: 'from-blue-500 to-purple-500' },
              { num: '2', title: 'Join a Session', desc: 'Browse educational sessions and join one that fits your learning goals. Live sessions and replays available 24/7.', bg: 'from-purple-500 to-pink-500' },
              { num: '3', title: 'Learn & Grow', desc: 'Apply what you learn, get feedback from the community, and watch your skills and sales grow together.', bg: 'from-pink-500 to-red-500' }
            ].map((step, i) => (
              <div key={i} className="text-center">
                <div className={`w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 bg-gradient-to-br ${step.bg} rounded-full flex items-center justify-center text-white text-2xl sm:text-3xl lg:text-4xl font-bold mx-auto mb-4 sm:mb-6 shadow-xl`}>
                  {step.num}
                </div>
                <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-gray-900">{step.title}</h3>
                <p className="text-gray-600 text-sm sm:text-base lg:text-lg">{step.desc}</p>
              </div>
            ))}
          </div>

          <div className="text-center">
            <button 
              onClick={() => window.open('https://listperfectly.com/pricing/', '_blank')}
              className="bg-gradient-to-r from-emerald-400 to-cyan-400 text-gray-900 hover:scale-105 transition-transform rounded-full px-8 sm:px-10 lg:px-12 py-3 sm:py-4 text-base sm:text-lg lg:text-xl font-bold shadow-xl"
            >
              Sign Up Now
            </button>
          </div>
        </div>
      </section>

      {/* What Makes LP Unique */}
      <section className="py-12 sm:py-16 lg:py-24 bg-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12 lg:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 text-gray-900">What Makes Listing Party Unique?</h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-4xl mx-auto px-4">
              We are the only crosslisting service that provides a safe online community where resellers grow from helping each other.
            </p>
      </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {[
              { icon: Video, title: 'Live 24/7 Education', desc: 'Join educational sessions whenever you need to learn. Someone\'s always teaching, no matter your schedule.' },
              { icon: MessageSquare, title: 'Expert Community', desc: 'Learn from experienced resellers who share proven strategies for every marketplace and listing challenge.' },
              { icon: Shield, title: 'Safe Learning Platform', desc: 'Clear community guidelines keep every session productive, respectful, and focused on education. No judgment, just growth.' },
              { icon: Sparkles, title: 'Built Into List Perfectly', desc: 'Seamless access with any LP plan. No extra cost, no extra login. Education amplifies your software experience.' }
            ].map((feature, i) => {
              const Icon = feature.icon
              return (
                <div key={i} className="p-6 sm:p-8 text-center bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl hover:shadow-xl transition-shadow border-2">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center text-white mx-auto mb-4 sm:mb-6">
                    <Icon className="w-6 h-6 sm:w-8 sm:h-8" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4 text-gray-900">{feature.title}</h3>
                  <p className="text-sm sm:text-base text-gray-600 leading-relaxed">{feature.desc}</p>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 sm:py-16 lg:py-24 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12 lg:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 text-gray-900">Frequently Asked Questions</h2>
          </div>

          <div className="max-w-3xl mx-auto space-y-3 sm:space-y-4">
            {faqData.map((faq, i) => (
              <div key={i} className="border-2 rounded-xl overflow-hidden hover:border-blue-500 transition-colors bg-white">
                <button 
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex justify-between items-center p-4 sm:p-6 text-left"
                >
                  <span className="text-base sm:text-lg font-semibold text-gray-900 pr-4">{faq.q}</span>
                  <ChevronDown className={`w-5 h-5 sm:w-6 sm:h-6 transition-transform flex-shrink-0 ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && (
                  <div className="px-4 sm:px-6 pb-4 sm:pb-6 text-sm sm:text-base text-gray-600 leading-relaxed">{faq.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-12 sm:py-16 lg:py-24 bg-gradient-to-br from-blue-900 via-purple-900 to-blue-800 text-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6 sm:mb-8">Ready to Start Learning?</h2>
            <p className="text-lg sm:text-xl lg:text-2xl text-gray-200 mb-8 sm:mb-12 px-4">Join thousands of resellers who've transformed their businesses through community support and continuous learning.</p>
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center px-4">
              <button 
                onClick={() => window.open('https://listperfectly.com/pricing/', '_blank')}
                className="bg-gradient-to-r from-emerald-400 to-cyan-400 text-gray-900 hover:scale-105 transition-transform rounded-full px-6 sm:px-8 lg:px-10 py-3 sm:py-4 text-sm sm:text-base lg:text-lg font-bold shadow-xl flex items-center justify-center gap-2"
              >
                <Play className="w-4 h-4 sm:w-5 sm:h-5 fill-current" />
                Start Free Trial with List Perfectly
              </button>
              <button 
                onClick={() => navigate('/login')}
                className="bg-white text-blue-900 rounded-full px-6 sm:px-8 lg:px-10 py-3 sm:py-4 text-sm sm:text-base lg:text-lg font-bold hover:scale-105 transition-transform shadow-xl"
              >
                Sign In to Listing Party
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 sm:py-12">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8 mb-6 sm:mb-8">
            <div>
              <div className="mb-4">
                <SVGUpdatedLogoC width={200} className="h-auto" />
              </div>
              <p className="text-gray-400 text-sm">A community of online resellers learning together, growing together, and succeeding together.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">List Perfectly</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li 
                  onClick={() => window.open('https://listperfectly.com/', '_blank')}
                  className="hover:text-white cursor-pointer transition-colors"
                >
                  About Us
                </li>
                <li 
                  onClick={() => window.open('https://listperfectly.com/pricing/', '_blank')}
                  className="hover:text-white cursor-pointer transition-colors"
                >
                  Pricing
                </li>
                <li 
                  onClick={() => window.open('https://listperfectly.com/pricing/', '_blank')}
                  className="hover:text-white cursor-pointer transition-colors"
                >
                  Sign Up
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-6 sm:pt-8 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs sm:text-sm text-gray-400">
            <div className="text-center sm:text-left">© Listing Party™ {new Date().getFullYear()}. All Rights Reserved.</div>
            <div className="flex flex-wrap gap-4 sm:gap-6 justify-center sm:justify-end">
              <span 
                onClick={() => navigate('/')}
                className="hover:text-white cursor-pointer transition-colors"
              >
                Privacy
              </span>
              <span 
                onClick={() => navigate('/')}
                className="hover:text-white cursor-pointer transition-colors"
              >
            Terms of Service
              </span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
